import { z } from 'zod';
import { ProfileAvatarDesign } from '../members/avatar.js';
export const SchedulingHostStatus = z.enum(['active', 'archived']);
export const SchedulingAvailabilityKind = z.enum(['1on1', 'group']);
export const SchedulingAvailabilityStatus = z.enum(['open', 'closed']);
export const SchedulingBookingStatus = z.enum(['confirmed', 'cancelled', 'waitlisted']);
export const SchedulingAttendanceStatus = z.enum([
    'unknown',
    'attended',
    'absent',
    'excused',
    'no_show',
]);
export const SchedulingPaymentStatus = z.enum([
    'not_required',
    'pending',
    'paid',
    'failed',
    'refunded',
]);
const Uuid = z.string().uuid();
const IsoDateTime = z.string().datetime({ offset: true });
export const SchedulingHost = z.object({
    id: Uuid,
    userId: Uuid,
    avatarDesign: ProfileAvatarDesign.optional(),
    slug: z.string().min(3).max(80),
    headline: z.string().min(1).max(160),
    description: z.string().max(2000).nullable(),
    sessionLengthMinutes: z.number().int().min(5).max(480),
    location: z.string().max(500).nullable(),
    tzid: z.string().min(1).max(64),
    visibleGroupIds: z.array(z.string()),
    status: SchedulingHostStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const SchedulingAvailability = z.object({
    id: Uuid,
    hostId: Uuid,
    hostSlug: z.string().min(3).max(80),
    hostUserId: Uuid,
    kind: SchedulingAvailabilityKind,
    title: z.string().min(1).max(180),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    capacity: z.number().int().min(1).max(500),
    bookedCount: z.number().int().min(0),
    waitlistCount: z.number().int().min(0),
    location: z.string().max(500).nullable(),
    notes: z.string().max(2000).nullable(),
    recurrence: z.string().max(500).nullable(),
    recurrenceGroupId: Uuid.nullable(),
    paymentRequired: z.boolean(),
    priceCents: z.number().int().min(0).nullable(),
    currency: z.string().length(3).nullable(),
    paymentUrl: z.string().max(1000).nullable(),
    videoProvider: z.string().max(80).nullable(),
    status: SchedulingAvailabilityStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const SchedulingBooking = z.object({
    id: Uuid,
    availabilityId: Uuid,
    userId: Uuid,
    attendeeName: z.string().min(1).max(160).nullable().optional(),
    attendeeAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    hostId: Uuid,
    hostSlug: z.string().min(3).max(80),
    title: z.string().min(1),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    topic: z.string().max(1000).nullable(),
    status: SchedulingBookingStatus,
    confirmationCode: z.string().min(1),
    attendanceStatus: SchedulingAttendanceStatus,
    attendedAt: IsoDateTime.nullable(),
    attendanceNote: z.string().max(1000).nullable(),
    paymentStatus: SchedulingPaymentStatus,
    paymentReference: z.string().max(300).nullable(),
    videoJoinUrl: z.string().max(1000).nullable(),
    cancelledAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const SchedulingHostResponse = z.object({ host: SchedulingHost });
export const SchedulingHostListResponse = z.object({
    hosts: z.array(SchedulingHost),
    total: z.number().int().min(0),
    limit: z.number().int().min(0),
    offset: z.number().int().min(0),
});
export const SchedulingAvailabilityResponse = z.object({
    availability: SchedulingAvailability,
});
export const SchedulingAvailabilityListResponse = z.object({
    availability: z.array(SchedulingAvailability),
    total: z.number().int().min(0),
    limit: z.number().int().min(0),
    offset: z.number().int().min(0),
});
export const SchedulingBookingResponse = z.object({
    booking: SchedulingBooking,
    promotedBooking: SchedulingBooking.nullable(),
});
export const SchedulingBookingListResponse = z.object({
    bookings: z.array(SchedulingBooking),
    total: z.number().int().min(0),
    limit: z.number().int().min(0),
    offset: z.number().int().min(0),
});
export const SCHEDULING_ERROR_CODES = {
    Disabled: 'SCHEDULING_DISABLED',
    WaitlistDisabled: 'SCHEDULING_WAITLIST_DISABLED',
    Unauthenticated: 'SCHEDULING_UNAUTHENTICATED',
    HostNotFound: 'SCHEDULING_HOST_NOT_FOUND',
    AvailabilityNotFound: 'SCHEDULING_AVAILABILITY_NOT_FOUND',
    BookingNotFound: 'SCHEDULING_BOOKING_NOT_FOUND',
    Forbidden: 'SCHEDULING_FORBIDDEN',
    SelfBooking: 'SCHEDULING_SELF_BOOKING',
    InvalidTimeRange: 'SCHEDULING_INVALID_TIME_RANGE',
    InPast: 'SCHEDULING_IN_PAST',
    AvailabilityConflict: 'SCHEDULING_AVAILABILITY_CONFLICT',
    CapacityFull: 'SCHEDULING_CAPACITY_FULL',
    DuplicateBooking: 'SCHEDULING_DUPLICATE_BOOKING',
    InvalidGroupIds: 'SCHEDULING_INVALID_GROUP_IDS',
    InvalidRecurrence: 'SCHEDULING_INVALID_RECURRENCE',
    RecurrenceTooLarge: 'SCHEDULING_RECURRENCE_TOO_LARGE',
    InvalidPayment: 'SCHEDULING_INVALID_PAYMENT',
};
export const SCHEDULING_ACTIVITY_KINDS = {
    HostCreated: 'host.created',
    AvailabilityOpened: 'availability.opened',
    AvailabilityClosed: 'availability.closed',
    BookingConfirmed: 'booking.confirmed',
    BookingCancelled: 'booking.cancelled',
    WaitlistJoined: 'waitlist.joined',
    AttendanceUpdated: 'booking.attendance-updated',
};
//# sourceMappingURL=scheduling.js.map